/** T87 — Subscription summary + upgrade-request client. */
import type { SaasPaymentIntent } from "./saasBilling";

/** Giá của một gói ĐỐI VỚI phòng khám đang đăng nhập. */
export interface PlanQuote {
  planId: string;
  planCode: string;
  /** Giá phải trả. */
  priceVnd: number;
  /** Giá niêm yết, để nói được "giá riêng của bạn" thay vì lặng lẽ hiện một con số khác. */
  listPriceVnd: number;
  contracted: boolean;
}

/** Kết quả mua bằng ví. */
export interface Purchase {
  periodId: string;
  planCode: string;
  priceVnd: number;
  startsAt: string;
  endsAt: string;
  walletBalanceVnd: number;
  extendedExistingPeriod: boolean;
}
import { apiClient } from "./client";

export interface PlanFeature {
  code: string;
  valueText: string | null;
  valueInt: number | null;
}

/** Các màn hình của MỘT nhóm menu mà gói gồm (ADR-0053). Nhóm gói không có màn hình nào thì vắng. */
export interface PlanScreenGroup {
  group: string;
  screens: string[];
}

export interface PlanSummary {
  id: string;
  code: string;
  /** Tên do quản trị viên nền tảng đặt — thứ hiển thị. `code` chỉ để gửi lại máy chủ. */
  name: string;
  monthlyPriceVnd: number;
  /** Hạn mức và module của gói — mọi thứ trừ màn hình. */
  features: PlanFeature[];
  /** Màn hình gói gồm, theo nhóm menu, bằng nhãn. */
  screens: PlanScreenGroup[];
}

export interface CurrentSubscription {
  planId: string;
  planCode: string;
  planName: string;
  monthlyPriceVnd: number;
  status: string;
  activeFrom: string;
  activeTo: string | null;
}

export interface UpgradeRequest {
  id: string;
  requestedPlan: string;
  message: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  status: string;
  createdAt: string;
}

export interface SubscriptionSummary {
  current: CurrentSubscription | null;
  plans: PlanSummary[];
  recentRequests: UpgradeRequest[];
}

export interface CreateUpgradeRequestInput {
  requestedPlan: string;
  message?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
}

const FEATURE_LABELS: Record<string, string> = {
  "limit.branches": "Số chi nhánh",
  "limit.users": "Số người dùng",
  "limit.storage-gb": "Dung lượng lưu trữ (GB)",
  "limit.prescriptions-per-month": "Đơn thuốc / tháng",
  "module.prescription": "Đơn thuốc điện tử",
  "module.inventory": "Quản lý kho thuốc",
  "module.bhyt-export": "Xuất BHYT",
  "module.multi-branch": "Nhiều chi nhánh",
  "module.ai-assistant": "Trợ lý AI",
  "module.advanced-reports": "Báo cáo nâng cao",
  "module.coupon": "Mã giảm giá",
  "module.membership": "Hạng thành viên",
  "module.plugins": "Tiện ích mở rộng",
  // #19589 mục 2: hai mã này có hằng ở backend (`EntitlementCode`) và được `@RequiresEntitlement`
  // dùng thật, nhưng chưa có nhãn ở đây — nên bảng so sánh gói in ra MÃ THÔ
  // ("module.einvoice") cho phòng khám đọc. Cùng lớp lỗi với việc trang thanh toán in "PRO"
  // (#19596): mã nội bộ không phải nhãn.
  "module.einvoice": "Hoá đơn điện tử",
  "module.gov-eprescription": "Đơn thuốc quốc gia",
  "sla.support-tier": "Mức hỗ trợ kỹ thuật",
};

export function featureLabel(code: string): string {
  return FEATURE_LABELS[code] ?? code;
}

export function statusLabel(status: string): string {
  switch (status) {
    case "ACTIVE":
      return "Đang hoạt động";
    case "TRIALING":
      return "Đang dùng thử";
    case "PAST_DUE":
      return "Quá hạn thanh toán";
    case "CANCELLED":
      return "Đã hủy";
    case "SUSPENDED":
      return "Tạm ngưng";
    case "OPEN":
      return "Đã gửi";
    case "IN_REVIEW":
      return "Đang xử lý";
    case "DONE":
      return "Đã xử lý";
    default:
      return status;
  }
}

/**
 * Báo giá nâng gói giữa kỳ, ĐÃ ĐÔNG CỨNG phía server (#18990).
 *
 * Mọi con số ở đây đều để giải thích được số tiền: chênh lệch toàn kỳ, thời gian còn lại, và số
 * phải trả. Màn hình KHÔNG tự tính lại — tính lại ở client là cách nhanh nhất để con số hiện ra
 * khác con số bị thu.
 */
export type UpgradeQuote = {
  intentId: string;
  fromPlanCode: string;
  toPlanCode: string;
  fromPriceVnd: number;
  toPriceVnd: number;
  fullDifferenceVnd: number;
  periodEndsAt: string;
  remainingSeconds: number;
  totalSeconds: number;
  amountVnd: number;
  walletBalanceVnd: number;
  walletCovers: boolean;
};

const UPGRADE = "/v1/clinic/subscription/upgrade";

export const subscriptionUpgradeApi = {
  /** Báo giá đang mở, hoặc null khi chưa có. */
  open: () =>
    apiClient
      .get<UpgradeQuote | "">(UPGRADE)
      .then((r) => (r.status === 204 || !r.data ? null : (r.data as UpgradeQuote))),
  quote: (planCode: string) =>
    apiClient.post<UpgradeQuote>(`${UPGRADE}/quote`, { planCode }).then((r) => r.data),
  payFromWallet: (intentId: string) =>
    apiClient.post<void>(`${UPGRADE}/${intentId}/pay-from-wallet`),
  paySePay: (intentId: string) =>
    apiClient.post<{ id: string; amountVnd: number }>(`${UPGRADE}/${intentId}/pay-with-sepay`)
      .then((r) => r.data),
  cancel: (intentId: string) => apiClient.post<void>(`${UPGRADE}/${intentId}/cancel`),
};

export const subscriptionUpgradeQueryKeys = {
  open: () => ["subscription-upgrade", "open"] as const,
};

export const subscriptionApi = {
  summary(): Promise<SubscriptionSummary> {
    return apiClient
      .get<SubscriptionSummary>("/v1/clinic/subscription")
      .then((r) => r.data);
  },
  submitUpgrade(req: CreateUpgradeRequestInput): Promise<UpgradeRequest> {
    return apiClient
      .post<UpgradeRequest>("/v1/clinic/subscription/upgrade-requests", req)
      .then((r) => r.data);
  },
  /**
   * Giá phòng khám NÀY phải trả cho từng gói bán được.
   *
   * Bảng chọn gói phải hỏi chỗ này chứ không lấy monthlyPriceVnd trong bảng giá: phòng khám có
   * giá thoả thuận riêng sẽ thấy giá niêm yết rồi bị thu một con số khác (#18990 P2.1).
   */
  quotes(): Promise<PlanQuote[]> {
    return apiClient.get<PlanQuote[]>("/v1/clinic/subscription/quotes").then((r) => r.data);
  },

  /**
   * Chuyển sang một gói miễn phí đang mở (ADR-0053) — không qua ví. Đây là đường ra khỏi chỉ-đọc
   * mà không cần nạp tiền. 409 khi còn kỳ đã trả tiền của một gói tính phí.
   */
  selectFreePlan(planCode: string): Promise<{ planCode: string; activeTo: string | null }> {
    return apiClient
      .post<{ planCode: string; activeTo: string | null }>("/v1/clinic/subscription/free-plan", {
        planCode,
      })
      .then((r) => r.data);
  },

  /** Mua bằng ví. 402 kèm số còn thiếu khi ví không đủ. */
  purchase(planCode: string): Promise<Purchase> {
    return apiClient
      .post<Purchase>("/v1/clinic/subscription/purchase", { planCode })
      .then((r) => r.data);
  },

  /**
   * Trả thẳng qua PayOS, không cần nạp ví trước (#18990 P2.2).
   *
   * Chỉ tạo link. Gói kích hoạt khi webhook báo tiền đã về — nên trang không được coi việc mở
   * được link là đã mua xong.
   */
  pay(planCode: string): Promise<SaasPaymentIntent> {
    return apiClient
      .post<SaasPaymentIntent>("/v1/clinic/subscription/pay", { planCode })
      .then((r) => r.data);
  },

  /** Đường THỬ, mặc định TẮT ở backend (404). Không dùng cho luồng mua thật. */
  subscribe(planCode: string): Promise<SubscriptionSummary> {
    return apiClient
      .post<SubscriptionSummary>("/v1/clinic/subscription/subscribe", { planCode })
      .then((r) => r.data);
  },
};

export const subscriptionQueryKeys = {
  summary: () => ["subscription-summary"] as const,
  quotes: () => ["subscription-quotes"] as const,
};
